import FileExchangePanel from '@/components/FileExchangePanel';
import { useI18n } from '@/i18n/I18nContext';
import { Skeleton, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { amountCell, amountHeader, dateCell } from 'src/components/DataTable';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from 'src/components/DataTable';
import Content from 'src/components/Content';
import { expenseService } from "@/service/PartnerExpenseService";
import IdAndNameData from 'src/data/IdAndNameData';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { PartnerExpense } from 'src/api/PartnerExpense';
import ReachLink from 'src/components/ReachLink';
import { FileDownload, FileSelect } from 'src/components/FileSelect';
import AddButton from 'src/components/buttons/AddButton';
import WaitingSpinner from '@/components/WaitingSpinner';
import { DropDownMenu } from '@/components/DropDownMenu';
import { formatPicker } from 'src/data/DateTimeUtil';
import { toaster } from '@/components/ui/toaster';

const OverviewExpenses = () => {
    const { t } = useI18n();
    const [data, setData] = useState<PartnerExpense[]>([]);
    const [projects, setProjects] = useState<Map<number, string>>(new Map()); // id to project name
    const [partners, setPartners] = useState<Map<number, string>>(new Map()); // id to partner name
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { open: isOpen, onOpen, onClose } = useDisclosure(); // for delete dialog

    const navigate = useNavigate();

    const onDelete = useCallback((id: number, name: string): Promise<void> => {
        console.info(`deleting ${id} ${name}`);
        return expenseService.remove(id)
            .then(() => {
                setData(prev => prev.filter(item => item.id !== id));
                setDeleteData(null);
                onClose();
                toaster.create({
                    title: 'Ausgabe gelöscht',
                    type: 'success'
                });
            });
    }, [onClose]);

    const onEdit = useCallback((id: number): void => {
        console.info(`editing ${id}`);
        navigate(`/partners/expenses/edit/${id}`);
    }, [navigate]);

    const onView = useCallback((id: number): void => {
        console.info(`viewing ${id}`);
        navigate(`/partners/expenses/view/${id}`);
    }, [navigate]);

    const loadAllData = (): Promise<void> => {
        setIsLoading(true);
        return expenseService.listAll().then(r => {
            setData(r.results.concat());
            setProjects(new Map(r.projects.map(p => [p.id, p.name])));
            setPartners(new Map(r.partners.map(p => [p.id, p.name])));
        })
            .finally(() => setIsLoading(false));
    }

    const onUploadFinished = useCallback((): Promise<void> => {
        console.info(`upload partners expenses suceeded...`);
        return loadAllData();
    }, []);

    useEffect(() => {
        loadAllData();
    }, [onUploadFinished]);

    const columns: ColumnDef<PartnerExpense>[] = useMemo(
        () => [
            {
                id: 'entryDate',
                header: 'Datum',
                accessorFn: (row: PartnerExpense) => row.entryDate,
                cell: dateCell()
            },
            {
                id: 'partner',
                header: 'Partner',
                accessorFn: (row: PartnerExpense) => row.partnerId,
                cell: (info: CellContext<PartnerExpense, number>) => {
                    let pid = info.getValue();
                    let partnerName = partners.get(pid);
                    return (<ReachLink to={`/partners/view/${pid}`} name={partnerName} tooltip='zu den Partnerdetails' />);
                },
                sortFn: (rowA: Row<PartnerExpense>, rowB: Row<PartnerExpense>, _): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    let ap = partners.get(a.partnerId);
                    let bp = partners.get(b.partnerId);
                    return ap.trim().localeCompare(bp.trim());
                },
            },
            {
                id: 'project',
                header: 'Projekt',
                accessorFn: (row: PartnerExpense) => row.projectId,
                cell: (info: CellContext<PartnerExpense, number>) => {
                    let pid = info.getValue();
                    let projectName = projects.get(pid);
                    return (<ReachLink to={`/projects/view/${pid}`} name={projectName} tooltip='zu den Projektdetails' />);
                },
                sortFn: (rowA: Row<PartnerExpense>, rowB: Row<PartnerExpense>, _): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    let ap = projects.get(a.projectId) ?? '';
                    let bp = projects.get(b.projectId) ?? '';
                    return ap.trim().localeCompare(bp.trim());
                },
            },
            {
                id: 'amount',
                header: amountHeader({ name: 'Summe' }),
                accessorKey: 'amount',
                cell: amountCell({}),
            },
            {
                id: 'currency',
                header: 'Währung',
                accessorKey: 'currency'
            },
            {
                id: 'comments',
                header: () => (<div style={{ width: '20rem' }}>Kommentar</div>),
                accessorKey: 'comments',
                enableSorting: false
            },
            {
                id: 'edit',
                enableSorting: false,
                cell: info => {
                    let row = info.row.original;
                    return (<DropDownMenu onMenuView={() => onView(row.id)} onMenuEdit={() => onEdit(row.id)} onMenuDelete={() => {
                        let da: IdAndNameData<number> = {
                            id: row.id,
                            name: `${projects.get(row.projectId)} / ${partners.get(row.partnerId)}`
                        };
                        setDeleteData(da);
                        onOpen();
                    }} />)
                }
            }
        ], [projects, partners, onEdit, onOpen, onView]
    );

    return (
        <Content
            title={t('overviews.expenses.title')}
            description={t('overviews.expenses.description')}
            actions={<>
                <FileExchangePanel>
                    <FileSelect path='/api/partners/expenses/upload' onUpload={onUploadFinished} />
                    <FileDownload scope='all' path='/api/partners/expenses/download' name={`ausgaben-${formatPicker(new Date())}.xlsx`} />
                </FileExchangePanel>
                <AddButton caption={t('overviews.expenses.create')} onClick={() => navigate('/partners/expenses/add')} />
            </>}
        >
            <Stack gap='6'>
                {isLoading ? (
                    <Stack py={5}>
                        <WaitingSpinner size='xl' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                    </Stack>
                ) : (
                    <>
                        <Text fontSize='sm' color='fg.muted' textAlign='end' aria-live='polite'>{t('common.table.results', { count: data.length })}</Text>
                        <DataTable columns={columns} data={data}
                            initialSortByColumn='entryDate' initialSortByDesc={true} hideColumnsOnXs={['entryDate', 'comments']}
                            onRowClick={row => onEdit(row.id)}
                        />
                        <ConfirmDialog
                            heading={`${deleteData?.name} Löschen`}
                            isOpen={isOpen}
                            onClose={onClose}
                            onConfirm={async (): Promise<void> => onDelete(deleteData.id, deleteData.name)}
                        />
                    </>
                )}
            </Stack>
        </Content>
    );
}

export default OverviewExpenses
